"use client";

import { ClockIcon, MailIcon, PhoneIcon } from "lucide-react";
import { SentryFeatureTags, logException } from "@punchup/sentry";
import { VenuePageEvents, trackEvent } from "@punchup/analytics";
import { VenuePagesRow, updateVenueHours } from "../..";

import Link from "next/link";
import { VenueHoursForm } from "./VenueHoursForm";
import { cn } from "@punchup/utils/cn";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { revalidatePathClient } from "@punchup/utils/vercel";
import { startTransition } from "react";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useToast } from "@punchup/ui-common/shadcn/ui/use-toast";

interface VenueHoursProps {
  venuePage: VenuePagesRow;
  canEdit?: boolean;
}

export const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export function VenueHoursDisplay({
  hours,
}: {
  hours: Record<string, string> | null;
}) {
  return (
    <>
      {DAYS_OF_WEEK.map((day) => {
        const time = hours?.[day as keyof typeof hours] || "Missing";
        const closed = time.toLowerCase() === "closed";
        return (
          <div key={day} className="flex flex-row gap-8 justify-between">
            <span className="text-base text-primary-blue">{day}</span>
            {closed ? (
              <span className="text-base font-medium text-red-500">Closed</span>
            ) : (
              <span className="text-base text-primary-blue">{time}</span>
            )}
          </div>
        );
      })}
    </>
  );
}

export default function VenueHours({
  venuePage: { id, slug, hours, phone, email },
  canEdit,
}: VenueHoursProps) {
  const { toast } = useToast();
  const router = useRouter();
  const supabase = createClientComponentClient();
  const [isEditing, setIsEditing] = useState(false);

  const handleUpdateHours = async (newHours: Record<string, string>) => {
    try {
      await updateVenueHours({
        supabase,
        venuePageId: id,
        newHours,
      });

      await revalidatePathClient(`/v/${slug}`);
      startTransition(() => {
        router.refresh();
      });
      setIsEditing(false);
      toast({
        title: "Success",
        variant: "success",
        description: "Venue hours updated successfully",
      });
    } catch (error) {
      logException(error, {
        extra: {
          venuePageId: id,
        },
        tags: {
          feature: SentryFeatureTags.Venues,
        },
      });
      toast({
        title: "Error",
        description: "Failed to update venue hours. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleEmailClick = () => {
    trackEvent(VenuePageEvents.CLICKED_CONTACT_EMAIL, {
      venueSlug: slug,
      email,
    });
  };

  const handlePhoneClick = () => {
    trackEvent(VenuePageEvents.CLICKED_CONTACT_PHONE_NUMBER, {
      venueSlug: slug,
      phone,
    });
  };

  if (!hours && !canEdit) return null;

  // Settings page layout (canEdit mode)
  if (canEdit) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-6">
          <VenueHoursForm
            hours={hours as Record<string, string> | null}
            isEditing={isEditing}
            onEdit={() => setIsEditing(true)}
            onCancel={() => setIsEditing(false)}
            onSubmit={handleUpdateHours}
          />
        </div>
      </div>
    );
  }

  // Public venue page layout
  return (
    <div className="flex flex-col gap-y-3 w-full rounded-xl border border-text-secondary px-5 py-4 md:p-6 bg-white">
      <h2 className="text-lg font-bold text-primary-blue">Venue info</h2>
      <div className="flex flex-col gap-y-6">
        <div className="flex flex-row gap-x-2">
          <div className="flex flex-col items-start justify-start">
            <ClockIcon className="w-4 h-4 mt-1 text-primary-blue" />
          </div>
          <div className="flex flex-col gap-y-4 items-start flex-1 w-full">
            <p className="text-base font-semibold text-primary-blue">Hours:</p>
            <div className="flex flex-col gap-y-4 w-full">
              <VenueHoursDisplay
                hours={hours as Record<string, string> | null}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-row gap-x-2">
          <div className="flex flex-col items-start justify-start">
            <PhoneIcon className="w-4 h-4 mt-1 text-primary-blue" />
          </div>
          <div className="flex flex-col gap-y-2 items-start flex-1 w-full">
            <p className="text-base font-semibold text-primary-blue">Phone:</p>
            <Link
              href={`tel:${phone}`}
              className="text-base text-primary-blue"
              onClick={handlePhoneClick}
            >
              {phone}
            </Link>
          </div>
        </div>
        <div className="flex flex-row gap-x-2">
          <div className="flex flex-col items-start justify-start">
            <MailIcon className="w-4 h-4 mt-1 text-primary-blue" />
          </div>
          <div className="flex flex-col gap-y-2 items-start flex-1 w-full">
            <p className="text-base font-semibold text-primary-blue">Email:</p>
            <Link
              href={`mailto:${email}`}
              className="text-base text-primary-blue"
              onClick={handleEmailClick}
            >
              {email}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
